import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import { createReference, resolveId } from "@medplum/core";
import { Encounter, Patient } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { useDisclosure } from "@mantine/hooks";

import * as Sentry from "@sentry/react";

import { BirthMilestones } from "@/encounters/components/RemindersAlertsSection/interfaces";
import {
  extractBirthMilestonesFromObservation,
  extractBirthMilestonesFromROMObservation,
} from "@/encounters/components/RemindersAlertsSection/utils/birthMilestones";
import { LaborFlowsheetForms } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { selectBirthDateTime, selectCurrentBirth } from "@/store/slices/birth";
import { showSystemErrorNotification } from "@/utils/notifications";

const INITIAL_BIRTH_MILESTONES: BirthMilestones = {
  rom: { name: "ROM" },
  headDelivered: { name: "Head Delivered" },
  bodyDelivered: { name: "Body Delivered" },
};

interface BirthMilestonesContextType {
  birthMilestones: BirthMilestones;
  isLoadingMilestones: boolean;
  setupRomMilestone: (milestoneData: { rom: string }) => void;
  setupHeadDeliveredMilestone: (milestoneData: { headDelivered: string }) => void;
  setupBodyDeliveredMilestone: (milestoneData: {
    bodyDelivered: string;
    updatedAtMs: number;
  }) => void;
}

const BirthMilestonesContext = createContext<BirthMilestonesContextType | null>(null);

interface BirthMilestonesProviderProps {
  children: ReactNode;
  encounter: Encounter;
}

export function BirthMilestonesProvider({ children, encounter }: BirthMilestonesProviderProps) {
  const medplum = useMedplum();
  const reduxBirthDateTime = useSelector(selectBirthDateTime);
  const reduxBirthObservation = useSelector(selectCurrentBirth);

  const [birthMilestones, setBirthMilestones] = useState<BirthMilestones>(INITIAL_BIRTH_MILESTONES);
  const [isLoadingMilestones, { open: startLoadingMilestones, close: stopLoadingMilestones }] =
    useDisclosure(false);

  // Track the most recent applied update time across all sources to ensure "last update wins"
  const lastBodyDeliveredAppliedAtMs = useRef<number | undefined>(undefined);

  // Reset local milestone state whenever the provider switches encounters so milestones from a
  // prior encounter (same client) cannot leak into the new encounter while Redux is rehydrating.
  const lastInitializedEncounterIdRef = useRef<string | undefined>(encounter?.id);
  useEffect(() => {
    if (lastInitializedEncounterIdRef.current === encounter?.id) return;
    lastInitializedEncounterIdRef.current = encounter?.id;
    setBirthMilestones(INITIAL_BIRTH_MILESTONES);
    lastBodyDeliveredAppliedAtMs.current = undefined;
  }, [encounter?.id]);

  const setupRomMilestone = useCallback((milestoneData: { rom: string }) => {
    setBirthMilestones((prev) => ({
      ...prev,
      rom: {
        ...prev.rom,
        startedAt: milestoneData.rom,
      },
    }));
  }, []);

  const setupHeadDeliveredMilestone = useCallback((milestoneData: { headDelivered: string }) => {
    setBirthMilestones((prev) => ({
      ...prev,
      rom: {
        ...prev.rom,
        completedAt: milestoneData.headDelivered,
      },
      headDelivered: {
        ...prev.headDelivered,
        startedAt: milestoneData.headDelivered,
      },
    }));
  }, []);

  const setupBodyDeliveredMilestone = useCallback(
    (milestoneData: { bodyDelivered: string; updatedAtMs: number }) => {
      if (
        lastBodyDeliveredAppliedAtMs.current !== undefined &&
        milestoneData.updatedAtMs <= lastBodyDeliveredAppliedAtMs.current
      ) {
        return;
      }

      lastBodyDeliveredAppliedAtMs.current = milestoneData.updatedAtMs;

      setBirthMilestones((prev) => ({
        ...prev,
        headDelivered: {
          ...prev.headDelivered,
          completedAt: milestoneData.bodyDelivered,
        },
        bodyDelivered: {
          ...prev.bodyDelivered,
          startedAt: milestoneData.bodyDelivered,
          completedAt: milestoneData.bodyDelivered,
        },
      }));
    },
    [],
  );

  useEffect(() => {
    if (!encounter) return;

    const fetchBirthMilestones = async () => {
      startLoadingMilestones();
      try {
        // Fetch birth observations
        const birthObservations = await medplum.searchResources("Observation", {
          encounter: createReference(encounter).reference,
          subject: createReference(encounter.subject as Patient).reference,
          code: `${Systems.QH}|${LaborFlowsheetForms.Birthing}`,
          status: "final",
          _sort: "date", // ensure we use headDelivered milestone data first
        });

        // Fetch shoulder dystocia observations (can also contain birth/body delivered time)
        await medplum.searchResources("Observation", {
          encounter: createReference(encounter).reference,
          subject: createReference(encounter.subject as Patient).reference,
          code: `${Systems.QH}|${LaborFlowsheetForms.ShoulderDystocia}`,
          status: "final",
          _sort: "date",
        });

        // Fetch ROM observation
        const romObservation = await medplum.searchOne("Observation", {
          encounter: createReference(encounter).reference,
          subject: createReference(encounter.subject as Patient).reference,
          code: `${Systems.QH}|${LaborFlowsheetForms.ROM}`,
          status: "final",
          _sort: "-date",
        });

        const romMilestoneData = extractBirthMilestonesFromROMObservation(romObservation);
        if (romMilestoneData?.rom) {
          setupRomMilestone(romMilestoneData as { rom: string });
        }

        birthObservations.forEach((observation) => {
          const milestoneData = extractBirthMilestonesFromObservation(observation);

          if (milestoneData.headDelivered) {
            setupHeadDeliveredMilestone(milestoneData as { headDelivered: string });
          }
          // bodyDelivered derived from Redux birthDateTime
        });
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      } finally {
        stopLoadingMilestones();
      }
    };

    fetchBirthMilestones();
  }, [
    encounter,
    medplum,
    startLoadingMilestones,
    stopLoadingMilestones,
    setupBodyDeliveredMilestone,
    setupHeadDeliveredMilestone,
    setupRomMilestone,
  ]);

  // Apply Redux birthDateTime updates with "now" as update timestamp to ensure last edit wins.
  // Skip when the Redux birth observation belongs to a different encounter (e.g., a prior L&D
  // encounter for the same client) so its bodyDelivered does not bleed into this encounter.
  useEffect(() => {
    if (!reduxBirthDateTime || !encounter?.id) return;
    const reduxBirthEncounterId = reduxBirthObservation?.encounter
      ? resolveId(reduxBirthObservation.encounter)
      : undefined;
    if (reduxBirthEncounterId && reduxBirthEncounterId !== encounter.id) return;
    setupBodyDeliveredMilestone({ bodyDelivered: reduxBirthDateTime, updatedAtMs: Date.now() });
  }, [reduxBirthDateTime, reduxBirthObservation, encounter?.id, setupBodyDeliveredMilestone]);

  const value: BirthMilestonesContextType = {
    birthMilestones,
    isLoadingMilestones,
    setupRomMilestone,
    setupHeadDeliveredMilestone,
    setupBodyDeliveredMilestone,
  };

  return (
    <BirthMilestonesContext.Provider value={value}>{children}</BirthMilestonesContext.Provider>
  );
}

export function useBirthMilestonesContext(): BirthMilestonesContextType {
  const context = useContext(BirthMilestonesContext);
  if (!context) {
    throw new Error("useBirthMilestonesContext must be used within a BirthMilestonesProvider");
  }
  return context;
}
